/**
 * 'firebase/firestore', with god-mode writes recorded.
 *
 * vite.config.js (and vitest.config.js) alias the bare specifier
 * 'firebase/firestore' to this file, so every `import { updateDoc } from
 * 'firebase/firestore'` in the app — 47 files today, every file tomorrow —
 * gets these versions without knowing it. Everything not redefined here is
 * re-exported from @firebase/firestore unchanged, which is exactly what
 * 'firebase/firestore' itself is (`export * from '@firebase/firestore'`).
 *
 * WHY AN ALIAS AND NOT A WRAPPER MODULE. The alternative — a src/lib/firestore
 * module that pages import instead — is 47 edits today and one forgotten
 * import tomorrow, and a page that forgot is a page whose god-mode saves are
 * silently denied by the rules (the audit is enforced server-side; forgetting
 * here does not weaken it, it just breaks the save). The alias makes the
 * correct behaviour the default for code nobody has written yet, the same
 * reasoning that put god mode on a single Firebase app rather than an injected
 * handle. The cost is indirection: a reader of a page cannot tell from the
 * import that writes are wrapped. That is why this file exists at the path a
 * grep for 'firebase/firestore' in vite.config.js leads to, and why ESLint
 * forbids importing '@firebase/firestore' directly anywhere else.
 *
 * WHAT A GOD-MODE WRITE BECOMES. One batch holding: the pointer
 * godmode_sessions/{sid}/meta/batch { nonce }; one entry per target write at
 * godmode_sessions/{sid}/writes/{nonce~key} with op, changed keys,
 * before/after and `at: serverTimestamp()`; and the target write(s). The
 * rules read the entry with getAfter() and deny the target write without it
 * — see godmodeAudited() in firestore.rules. Writes in a normal session pass
 * straight through to the SDK: same call, same promise, same errors.
 *
 * Before-values come from a read of the document just before the batch — a
 * god-mode session has the target's read access, and a fix you cannot see the
 * previous state of is not much of an audit. That read also tells a setDoc
 * whether it is a create or an update, which the rules check.
 */

export * from '@firebase/firestore';

import {
  doc as realDoc,
  getDoc as realGetDoc,
  setDoc as realSetDoc,
  updateDoc as realUpdateDoc,
  deleteDoc as realDeleteDoc,
  addDoc as realAddDoc,
  writeBatch as realWriteBatch,
  runTransaction as realRunTransaction,
} from '@firebase/firestore';

import {
  GODMODE_BATCH_CHUNK,
  resolveGodmodeContext,
  assertAuditable,
  newNonce,
  pointerRef,
  entryRef,
  keyForPath,
  updateArgsToObject,
  changedKeysOf,
  expandDotted,
  sanitizeValue,
  snapshotFields,
  capPayload,
  buildEntry,
} from './godmodeWriteContext';

// ---------------------------------------------------------------------------
// Describing a pending write
// ---------------------------------------------------------------------------

/**
 * Read the document a write is about to touch. Permission to read is the
 * target's; if they cannot read it, the entry records no `before` and the
 * op is guessed from the call (updateDoc/deleteDoc imply existence).
 */
async function readBefore(ref) {
  try {
    const snap = await realGetDoc(ref);
    return { known: true, exists: snap.exists(), data: snap.exists() ? snap.data() : null };
  } catch (err) {
    console.warn(`[godmode] could not read ${ref.path} before writing; recording without before-state:`, err?.code || err);
    return { known: false, exists: null, data: null };
  }
}

/**
 * The audit entry for one pending op, given the document's prior state.
 *
 *   set   → create if the doc is absent, else update; changed = keys written
 *           (+ keys of the old doc when overwriting, since those vanish too)
 *   update→ update; changed = top-level names of the paths written
 *   delete→ delete; changed = [] ; before = whole prior doc
 */
function describe(ctx, op, before) {
  const { ref } = op;
  if (op.type === 'delete') {
    return buildEntry(ctx, {
      op: 'delete', path: ref.path, changed: [],
      before: capPayload(sanitizeValue(before.data)), after: null,
    });
  }
  if (op.type === 'update') {
    const data = updateArgsToObject(op.args);
    const changed = changedKeysOf(data);
    return buildEntry(ctx, {
      op: 'update', path: ref.path, changed,
      before: snapshotFields(before.data, changed),
      after: capPayload(sanitizeValue(expandDotted(data))),
    });
  }
  // set
  const data = op.data || {};
  const merging = !!(op.options && (op.options.merge || op.options.mergeFields));
  let changed = op.options?.mergeFields
    ? Array.from(new Set(op.options.mergeFields.map((f) => (typeof f === 'string' ? f.split('.')[0] : String(f)))))
    : Object.keys(data);
  if (!merging && before.data) {
    // A plain set replaces the document: every old key not re-supplied is
    // affected too, and the rules' affectedKeys() will say so.
    changed = Array.from(new Set([...changed, ...Object.keys(before.data)]));
  }
  const isCreate = before.known ? !before.exists : false;
  return buildEntry(ctx, {
    op: isCreate ? 'create' : 'update', path: ref.path, changed,
    before: isCreate ? null : snapshotFields(before.data, changed),
    after: capPayload(sanitizeValue(data)),
  });
}

/** Apply recorded ops to a real WriteBatch or Transaction. */
function applyOps(target, ops) {
  ops.forEach((op) => {
    if (op.type === 'set') {
      if (op.options !== undefined) target.set(op.ref, op.data, op.options);
      else target.set(op.ref, op.data);
    } else if (op.type === 'update') {
      target.update(op.ref, ...op.args);
    } else if (op.type === 'delete') {
      target.delete(op.ref);
    }
  });
}

/**
 * Commit `ops` as audited god-mode batches of at most GODMODE_BATCH_CHUNK
 * target writes each. Each chunk is atomic with its own entries.
 */
async function commitAudited(db, ctx, ops) {
  assertAuditable(ctx);
  for (let i = 0; i < ops.length; i += GODMODE_BATCH_CHUNK) {
    const chunk = ops.slice(i, i + GODMODE_BATCH_CHUNK);
    const befores = await Promise.all(chunk.map((op) => readBefore(op.ref)));
    const batch = realWriteBatch(db);
    const nonce = newNonce();
    batch.set(pointerRef(db, ctx), { nonce });
    chunk.forEach((op, j) => {
      batch.set(entryRef(db, ctx, nonce, keyForPath(op.ref.path)), describe(ctx, op, befores[j]));
    });
    applyOps(batch, chunk);
    await batch.commit();
  }
}

// ---------------------------------------------------------------------------
// The single-document functions
// ---------------------------------------------------------------------------

export async function setDoc(ref, data, options) {
  const ctx = await resolveGodmodeContext(ref.firestore.app);
  if (!ctx) return options === undefined ? realSetDoc(ref, data) : realSetDoc(ref, data, options);
  return commitAudited(ref.firestore, ctx, [{ type: 'set', ref, data, options }]);
}

export async function updateDoc(ref, ...args) {
  const ctx = await resolveGodmodeContext(ref.firestore.app);
  if (!ctx) return realUpdateDoc(ref, ...args);
  return commitAudited(ref.firestore, ctx, [{ type: 'update', ref, args }]);
}

export async function deleteDoc(ref) {
  const ctx = await resolveGodmodeContext(ref.firestore.app);
  if (!ctx) return realDeleteDoc(ref);
  return commitAudited(ref.firestore, ctx, [{ type: 'delete', ref }]);
}

export async function addDoc(colRef, data) {
  const ctx = await resolveGodmodeContext(colRef.firestore.app);
  if (!ctx) return realAddDoc(colRef, data);
  // addDoc is a set on a fresh id; the id has to be known to name the entry.
  const ref = realDoc(colRef);
  await commitAudited(colRef.firestore, ctx, [{ type: 'set', ref, data }]);
  return ref;
}

// ---------------------------------------------------------------------------
// writeBatch
// ---------------------------------------------------------------------------

/**
 * A WriteBatch that records what it is asked to do. In a normal session the
 * real batch it fed all along is committed, so argument validation still
 * throws at the call site exactly as before. In god mode the recorded ops
 * are re-issued as audited chunks and the eager batch is dropped unused.
 */
class AuditedWriteBatch {
  constructor(db) {
    this._db = db;
    this._eager = realWriteBatch(db);
    this._ops = [];
  }

  set(ref, data, options) {
    if (options !== undefined) this._eager.set(ref, data, options);
    else this._eager.set(ref, data);
    this._ops.push({ type: 'set', ref, data, options });
    return this;
  }

  update(ref, ...args) {
    this._eager.update(ref, ...args);
    this._ops.push({ type: 'update', ref, args });
    return this;
  }

  delete(ref) {
    this._eager.delete(ref);
    this._ops.push({ type: 'delete', ref });
    return this;
  }

  async commit() {
    const ctx = await resolveGodmodeContext(this._db.app);
    if (!ctx) return this._eager.commit();
    if (this._ops.length === 0) return undefined;
    return commitAudited(this._db, ctx, this._ops);
  }
}

export function writeBatch(db) {
  return new AuditedWriteBatch(db);
}

// ---------------------------------------------------------------------------
// runTransaction
// ---------------------------------------------------------------------------

/**
 * In god mode the transaction's own writes are recorded and, after the
 * caller's function returns, the pointer and entries are added to the same
 * transaction. Transactions must do all reads before any write, so
 * before-values come only from documents the function itself read via
 * tx.get(); others are recorded without one. A transaction with more than
 * GODMODE_BATCH_CHUNK writes is still committed as one unit (it cannot be
 * split without changing its meaning) and may hit the rules' access budget.
 */
export async function runTransaction(db, updateFunction, options) {
  const ctx = await resolveGodmodeContext(db.app);
  if (!ctx) {
    return options === undefined
      ? realRunTransaction(db, updateFunction)
      : realRunTransaction(db, updateFunction, options);
  }
  assertAuditable(ctx);

  const run = (tx) => {
    const ops = [];
    const reads = new Map();
    const proxy = {
      async get(ref) {
        const snap = await tx.get(ref);
        reads.set(ref.path, { known: true, exists: snap.exists(), data: snap.exists() ? snap.data() : null });
        return snap;
      },
      set(ref, data, opts) {
        if (opts !== undefined) tx.set(ref, data, opts); else tx.set(ref, data);
        ops.push({ type: 'set', ref, data, options: opts });
        return proxy;
      },
      update(ref, ...args) {
        tx.update(ref, ...args);
        ops.push({ type: 'update', ref, args });
        return proxy;
      },
      delete(ref) {
        tx.delete(ref);
        ops.push({ type: 'delete', ref });
        return proxy;
      },
    };
    return Promise.resolve(updateFunction(proxy)).then((result) => {
      if (ops.length) {
        const nonce = newNonce();
        tx.set(pointerRef(db, ctx), { nonce });
        ops.forEach((op) => {
          const before = reads.get(op.ref.path) || { known: false, exists: null, data: null };
          tx.set(entryRef(db, ctx, nonce, keyForPath(op.ref.path)), describe(ctx, op, before));
        });
      }
      return result;
    });
  };

  return options === undefined ? realRunTransaction(db, run) : realRunTransaction(db, run, options);
}
